import {
  type CreateDispenserData,
  DispenserDetailResponse,
  dispenserDetailResponseSchema,
  DispenserListItem,
  dispenserListResponseSchema,
  dispenserTimelineDetailResponseSchema,
  dispenserTimelineResponseSchema,
  lastEventsResponseSchema,
  TimelineDetailRequestParams,
  UpdateDispenserData,
  ValidateSnData,
  validateSnResponseSchema,
} from '@/features/dispensers/services/schemas.ts';
import { PosService } from '@/features/pos/services/PosService.ts';
import { CursorRequestParams, ListResponse } from '@/common/api/schemas.ts';
import { GridState, gridStateToSieveParams } from '@/common/api/sieveAdapter.ts';
import { ApiClient } from '@/common/api/ApiClient.ts';

export const DispenserService = {
  create: async (data: CreateDispenserData): Promise<void> => {
    await ApiClient.post('/equipments', data);
  },
  update: async (data: UpdateDispenserData): Promise<void> => {
    await ApiClient.put('/equipments', data);
  },
  delete: async (id: string): Promise<void> => {
    await ApiClient.delete(`/equipments/${id}`);
  },
  detail: async (id: string) => {
    const data = await ApiClient.get(`/equipments/${id}`, dispenserDetailResponseSchema);

    const posList = await PosService.list();
    const pos = posList.result.find((posItem) => posItem.id === Number(data.pos));

    return {
      ...data,
      pos: pos ?? null,
    } satisfies DispenserDetailResponse;
  },
  list: async (gridState?: GridState): Promise<ListResponse<DispenserListItem>> => {
    const sieveParams = gridStateToSieveParams(gridState ?? {});
    const data = await ApiClient.get('/equipments', dispenserListResponseSchema, sieveParams);

    const posList = await PosService.list();

    return {
      ...data,
      result: data.result.map((item) => {
        const pos = posList.result.find((posItem) => posItem.id === Number(item.pos));
        return {
          ...item,
          pos: pos?.name ?? null,
          country: pos?.country ?? null,
        };
      }),
    };
  },
  lastEvents: (dispenserId: string) => {
    return ApiClient.get(`/equipments/${dispenserId}/last-events`, lastEventsResponseSchema);
  },
  validateSn: async (data: ValidateSnData) => {
    return await ApiClient.post(`/equipments/validate/sn`, data, validateSnResponseSchema);
  },
  timeline: async (dispenserId: string, params: CursorRequestParams) => {
    return await ApiClient.get(
      `/equipments/${dispenserId}/timeline`,
      dispenserTimelineResponseSchema,
      params
    );
  },
  timelineDetail: async (dispenserId: string, params: TimelineDetailRequestParams) => {
    return await ApiClient.get(
      `/equipments/${dispenserId}/timeline/detail`,
      dispenserTimelineDetailResponseSchema,
      params
    );
  },
};
